'use client';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';

import { useId, type ReactNode } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { AccountCard } from '@eliadmin/core/src/components/dlz/account/account-card';
import { OrderInfoCards } from '@eliadmin/core/src/components/dlz/account/orders/order-info';
import { OrderItemsCard } from '@eliadmin/core/src/components/dlz/account/orders/order-items';
import {
  CancelledNote,
  OrderStatusPill,
  OrdersMessage,
  ReorderMessage,
  TEXT_ACTION,
} from '@eliadmin/core/src/components/dlz/account/orders/order-parts';
import {
  orderStage,
  STAGE_STEP,
  type OrderStage,
} from '@eliadmin/core/src/components/dlz/account/orders/order-stage';
import { ORDERS_COPY } from '@eliadmin/core/src/components/dlz/account/orders/orders-copy';
import {
  orderDate,
  orderNumber,
  orderTime,
  useCustomerOrders,
  useOrderInfo,
} from '@eliadmin/core/src/components/dlz/account/orders/orders-data';
import { useOrderDocument } from '@eliadmin/core/src/components/dlz/account/orders/use-order-document';
import { useReorder } from '@eliadmin/core/src/components/dlz/account/orders/use-reorder';
import { productCount } from '@eliadmin/core/src/components/dlz/checkout/cart-math';
import { OrderStatus } from '@eliadmin/core/src/components/dlz/checkout/confirmation/order-status';
import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { getSlugFromPage } from '@eliadmin/core/src/utils/url-utils';

/**
 * Figma „Back“ (60324:20797): caret and DLZ/Body/M Medium in text/brand. Under `lg` it takes the
 * place and the look of the account's „‹ page“ link (Figma 60459:91418: 20 px caret,
 * DLZ/Heading/H5).
 */
function BackLink() {
  return (
    <Link
      href={ROUTES.ORDERS()}
      prefetch={false}
      className="flex w-fit items-center gap-2.5 rounded-sm text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] lg:gap-1.5"
    >
      <Icon name="caret-left" size={20} className="lg:size-[18px]" />
      <span className="dlz-h5 lg:dlz-body-m-medium">{ORDERS_COPY.back}</span>
    </Link>
  );
}

/**
 * Figma „Header“ (60324:20801; phones 60459:91421 + 60459:92257): number with the status pill, the
 * creation time and product count, „Faktúra (PDF)“ and „Kúpiť znova“. In a narrow column the title
 * is DLZ/Heading/H2 with the pill under the DLZ/Body/M meta line and the buttons share the width.
 */
function DetailHeader({
  order,
  info,
  stage,
}: {
  order: CustomerOrder;
  info: OrderInfo;
  stage: OrderStage;
}) {
  const { t } = useTranslations();
  const { document, download, loading } = useOrderDocument(info.documents);
  const reorder = useReorder(order);
  const button = 'h-12 min-w-0 flex-1 md:h-[52px] @[30rem]:flex-none';

  return (
    <div className="flex flex-col gap-6 @[30rem]:gap-4">
      <header className="flex flex-col gap-6 @[30rem]:flex-row @[30rem]:flex-wrap @[30rem]:items-center @[30rem]:justify-between @[30rem]:gap-x-6 @[30rem]:gap-y-4">
        {/* 30rem basis: the buttons stay beside the title only while both fit (the 877 px column). */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 @[30rem]:flex-[1_1_30rem] @[30rem]:gap-y-3">
          <h1 className="dlz-h2 @[30rem]:dlz-h3 min-w-0 basis-full text-[#0F1012] @[30rem]:basis-auto">
            {ORDERS_COPY.order(orderNumber(order))}
          </h1>
          <OrderStatusPill
            name={info.status.name}
            stage={stage}
            className="order-last @[30rem]:order-none"
          />
          <p className="dlz-body-m @[30rem]:dlz-body-s basis-full text-[#4F545C] @[30rem]:text-[#6B7079]">
            {ORDERS_COPY.createdAt(orderDate(info.created_at), orderTime(info.created_at))} ·{' '}
            {productCount(info.items.length, t)}
          </p>
        </div>
        {document || reorder.available ? (
          <div className="flex gap-3">
            {document ? (
              <Button variant="secondary" onClick={download} isLoading={loading} className={button}>
                {ORDERS_COPY.document(document.name || ORDERS_COPY.documentFallback)}
              </Button>
            ) : null}
            {reorder.available ? (
              <Button
                onClick={reorder.reorder}
                isLoading={reorder.pending}
                disabled={reorder.disabled}
                className={button}
              >
                {ORDERS_COPY.reorder}
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>
      <ReorderMessage result={reorder.result} />
    </div>
  );
}

/**
 * Figma „Help“ (60324:20996; phones 60459:92377): bg/surface box with the return and claim links.
 * Both lead to the store's claim page (returns and claims are one form there).
 */
function HelpBox() {
  const titleId = useId();
  const claim = getSlugFromPage('claim');

  return (
    <section
      aria-labelledby={titleId}
      className="flex flex-col gap-4 rounded-[18px] bg-[#F5F6F7] p-4 @[40rem]:flex-row @[40rem]:items-center md:px-6 md:py-5"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h2 id={titleId} className="dlz-h5 text-[#0F1012]">
          {ORDERS_COPY.help.title}
        </h2>
        <p className="dlz-body-s text-[#4F545C]">{ORDERS_COPY.help.text}</p>
      </div>
      <div className="flex items-center gap-4">
        <Link href={claim} prefetch={false} className={TEXT_ACTION}>
          {ORDERS_COPY.help.return}
        </Link>
        <Link href={claim} prefetch={false} className={TEXT_ACTION}>
          {ORDERS_COPY.help.claim}
        </Link>
      </div>
    </section>
  );
}

/**
 * Figma „Detail objednávky“ (60324:20208, phones 60459:91415): back link, header, „Stav objednávky“
 * with the four-step track (a cancelled order gets its cancellation line instead), the four info
 * cards, the products with the price summary and the help box. The order comes from the customer's
 * order list (it proves the order is theirs and carries the variants and product ids for „Kúpiť
 * znova“), the addresses and prices from `/orders/info`. „Sledovať zásielku“ and the times of the
 * later steps are left out – the API has no tracking link and no estimates.
 */
export function OrderDetail({ identifier }: { identifier: string }) {
  const { t } = useTranslations();
  const orders = useCustomerOrders();
  const info = useOrderInfo(identifier);
  const order = orders.data?.find(item => orderNumber(item) === identifier);

  // The customer's own list decides whether the order exists; the detail request only fills it in.
  const detail = order ? info : null;
  let content: ReactNode;
  if (orders.isError || detail?.isError) {
    content = (
      <OrdersMessage
        icon="warning"
        title={ORDERS_COPY.detailFailed}
        heading="h1"
        alert
        action={
          <Button
            variant="secondary"
            onClick={() => Promise.all([orders.refetch(), info.refetch()])}
            isLoading={orders.isRefetching || info.isRefetching}
          >
            {t('text595')}
          </Button>
        }
      />
    );
  } else if (orders.isPending || detail?.isPending) {
    content = <GenericLoader className="h-80" />;
  } else if (!order || !info.data) {
    content = (
      <OrdersMessage
        icon="package"
        title={ORDERS_COPY.notFound.title}
        text={ORDERS_COPY.notFound.text}
        heading="h1"
        action={
          <Button asChild variant="secondary">
            <Link href={ROUTES.ORDERS()} prefetch={false}>
              {ORDERS_COPY.back}
            </Link>
          </Button>
        }
      />
    );
  } else {
    const stage = orderStage(info.data);
    content = (
      <>
        <DetailHeader order={order} info={info.data} stage={stage} />
        <div className="flex flex-col gap-4 @[30rem]:gap-6">
          <AccountCard title={t('text245')}>
            {stage === 'cancelled' ? (
              <CancelledNote status={info.data.status.name} />
            ) : (
              <OrderStatus
                createdAt={info.data.created_at}
                t={t}
                current={STAGE_STEP[stage]}
                layout="card"
              />
            )}
          </AccountCard>
          <OrderLoyaltyReward reward={info.data.loyalty_reward} />
          <OrderInfoCards info={info.data} stage={stage} />
          <OrderItemsCard order={order} info={info.data} />
          <HelpBox />
        </div>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <BackLink />
      {content}
    </div>
  );
}
